// for statistical model of QA/Home page
export interface BehandletStatistikk {
  periods: Period[];
}

export interface PeriodDetail {
  kilde: string;
  antallBehandlet: number;
  antallAvist: number;
}

export interface Period {
  periodName: string;
  group: string;
  antallBehandlet: number;
  antallAvist: number;
  details: PeriodDetail[];
}

export interface Statistikk {
  antallAvviste: number;
  antallAktive: number;
  antallFolgesOpp: number;
  antallBehandlet: number;
  perKilde: {
    kilde: string;
    antallAktive: number;
    antallFolgesOpp: number;
  }[];
}

export interface SearchFilter {
  status: string;
  tags: string[];
  query: string;
  source: string;
  orderBy: string;
  take: number;
  leverandorKundenummerHosTsKundeId?: string;
  grossistKundenummerHosGln: string[];
}

export interface Filter {
  id: string;
  eierGln: string;
  organisasjonsnummer: string;
  comment: string;
  changedBy: string;
  lastChanged?: Date;
}

export interface EndringsMelding {
  behandlesAv: BehandlesAv[];
  folgesOppAv: {
    changedBy: string;
    comment?: string;
    status: string;
    statusChangedAt: Date;
  };
  id: string;
  lastChangedBy: string;
  changesMightBeRelatedToEnhetId?: number;
  changesMightBeRelatedToLopenummer?: string;
  changesUsedInEnhetWithLopenummer?: string;
  changesUsedInEnhetWithEnhetId?: string;
  description: string;
  enhetId?: string;
  lopenummer?: string;
  lastVasketAt: Date;
  recievedAt: Date;
  sentBy: string;
  sentBySystem: string;
  contactEmail: string;
  title: string;
  raw: any;
  status: string;
  statusLog: {
    changedBy: string;
    comment?: string;
    status: string;
    statusChangedAt: Date;
  }[];
  // Ekstra info om medlemhos feltene
  membershipSuggestions?: MembershipSuggestions | null;
  changes: {
    besoksadresse: QaAddress;
    fakturaAdresse?: QaAddress;
    postadresse?: QaAddress;
    bransjeForslag: string;
    enhetnavn: string;
    gln: string;
    eierGLN: string;
    eierGLNNavn: string;
    grossistKundenummer?: string;
    grossistKundenummerHos?: string;
    grossistKundenummerHosGln?: string;
    juridisknavn: string;
    kommentar: string;
    leverandorKundenummer: string;
    leverandorKundenummerHos: string;
    leverandorKundenummerHosTsKundeId: string;
    lopenr?: string;
    nedlagtDato?: Date;
    organisasjonsnr: string;
    telefon: string;
    kontonummer?: string;
    salgsareal?: number;
    oppstartsDato?: Date;
    fakturaEpost?: string;
    epost?: string;
    hjemmeside?: string;
    dagligLeder?: string;
    omsetning?: number;
    // Nye medlemhos-felt
    medlemHos?: string;
    medlemsNummer?: string;
    medlemFraOgMedDato?: Date | null;
    medlemTilOgMedDato?: Date | null;

    validations: {
      fieldName: string;
      fieldValue: string;
      validationResults: {
        isValueValid: boolean;
        occuredAt: Date;
        resultat: string;
        source: string;
        validationMessage: string;
      }[];
    }[];
  };
}

// QaApi prøver å finne kjedenr/enhetsgruppenr utifra medlemstekster i endringsmeldingen
export interface MembershipSuggestions {
  markedsKjede: MembershipSuggestion;
  regionalKjede: MembershipSuggestion;
  innkjopssamarbeid: MembershipSuggestion;
}

export interface MembershipSuggestion {
  nummer: string;
  navn: string;
}

export interface BehandlesAv {
  navn: string;
  time: Date;
}

export interface QaAddress {
  eastingUtmZone33N?: number;
  gateadresse: string;
  latitude?: number;
  longitude?: number;
  northingUtmZone33N?: number;
  landkode: string;
  postnr: string;
  poststed: string;
  formattedForOneLine: string;
  adressetype: 'Besøksadresse' | 'FakturaAdresse' | 'Postadresse' | 'Leveringsadresse';
  validations: {
    fieldName: string;
    fieldValue: string;
    validationResults: {
      isValueValid: boolean;
      occuredAt: Date;
      resultat: string;
      source: string;
      validationMessage: string;
    }[];
  }[];
}

export interface BehandletAction {
  id: string;
  lopenummer?: string;
  changedBy: string;
  enhetId?: number;
}

export interface BehandlesAction {
  id: string;
  approvedByusername: string;
}

export interface UpsertFilterAction {
  id: string;
  eierGln: string;
  organisasjonsnummer: string;
  comment: string;
  changedBy: string;
}

export class ChangeRequestStatus {
  static Ubehandlet = 'ubehandlet';
  static FolgesOpp = 'folgesOpp';
  static Behandlet = 'behandlet';
  static Avvist = 'avvist';
  static Alle = 'alle';
}

export interface RegistrerCommand {
  senderId: string;
  enhetInfo: EnhetEndringInfo;
  description: string;
  pushNotificationUrl: string;
  sentBy: string;
  sentBySystem: string;
  externalId?: string;
  contactEmail: string;
}

export interface FileUploadResponse {
  membershipOwner: MembershipOwner;
  meldinger: EnhetEndringInfo[];
  membershipDocumentChainId: string;
}

export interface MembershipOwner {
  id: string;
  name: string;
  krIdentifier: string;
  membershipType: string;
}

export interface EnhetEndringInfo {
  lopenummer?: number;
  gln: string;
  enhetNavn: string;
  firmaNavn: string;
  organisasjonsnummer: string;
  uniqueKey?: string;
  besokAdresse: string;
  besokPostnummer: string;
  besokPoststed: string;
  telefon: string;
  nedlagtDato?: Date | null;
  fakturaAdresse: string;
  fakturaPostnummer: string;
  fakturaPoststed: string;
  postAdresse: string;
  postnummer: string;
  postPoststed: string;
  kommentar: string;
  leverandorKundenummer: string;
  leverandorKundenummerHos: string;
  leverandorKundenummerHosTsKundeId: string;
  grossistKundenummer: string;
  grossistKundenummerHos: string;
  grossistKundenummerHosGln: string;
  bransjeForslag: string;
  dagligLeder: string;

  // nye felter
  kontonummer?: string;
  salgsareal?: number;
  oppstartsdato?: Date | null;
  fakturaEpost?: string;
  epost?: string;
  omsetning?: number;

  // kjede fields
  markedskjede: string;
  markedskjedeNr: string;
  markedskjedeAvdelingsNr?: string;
  regionalkjede: string;
  regionalkjedeNr: string;
  regionalkjedeAvdelingsNr?: string;

  // New fields
  medlemHos?: string;
  medlemsNummer?: string;
  medlemFraOgMedDato?: Date | null;
  medlemTilOgMedDato?: Date | null;
}

export interface SendToQaCommand {
  membershipOwnerId: string;
  meldingerToSend: EnhetEndringInfo[];
  membershipDocumentChainId?: string;
  sentBy?: string;
}

export interface MembershipDocumentDto {
  chainId: string;
  ownerId: string;
  ownerName: string;
  readAt: Date;
  members: EnhetEndringInfo[];
  sentToQaAt?: Date | null;
  sentToQaBy?: string | null;
  endringsMeldingIdsSent: string[];
  uniqueKeysSent: string[];
  totalSequences: number;
  uploadedFileUrl?: string;
}

export interface MembershipDocumentOverviewDto {
  id: string;
  chainId: string;
  ownerId: string;
  ownerName: string;
  readAt: Date;
  sentToQaAt?: Date | null;
  sentToQaBy?: string | null;
  totalSequences: number;
  totalMembers: number;
}

export interface MembershipOwnerDto {
  id: string;
  name: string;
}
